import type { StatusReportResponse } from "@msc/shared";

import { formatEur, formatHours, formatMonth } from "./format.js";

/**
 * Der **kundengerichtete Klartext** des Statusberichts — für die
 * Zwischenablage (Fallback `text/plain`) und für den `mailto:`-Entwurf.
 *
 * Bewusst ein eigenes Modul und nicht mehr in der Seite (Befund B4/B5/B6): Was
 * hier entsteht, landet wörtlich beim Kunden. Es gehört damit zu den Dingen,
 * die ein Test festhalten muss, und ein Test soll dafür keine React-Seite
 * aufbauen müssen.
 *
 * Die Sprache ist fest deutsch (Fachkonzept §13.1) — die Aufrufer reichen ein
 * `t` herein, das auf `REPORT_LANGUAGE` festgenagelt ist, nicht auf die
 * Oberflächensprache des Absenders.
 */

/** Übersetzer-Funktion, wie sie `i18n.getFixedT` liefert. */
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Der Bericht als Klartext.
 *
 * Vollständig wie die HTML-Fassung (Befund U3): Zuvor bestand der Text nur aus
 * Budget, Stunden und Tickets — Ansprechpartner und Veranstaltungen fehlten,
 * der Entwurf war 369 Zeichen lang und damit kein Bericht. Und er spricht
 * dieselben Wörter wie die HTML-Fassung: „Gebuchte Stunden" hieß hier
 * „Faktura-Stunden", also Interndeutsch beim Kunden (Befund U5).
 */
export function renderReportText(
  report: StatusReportResponse,
  locale: string,
  t: Translate,
): string {
  const lines: string[] = [];
  const eur = (v: number | null) => formatEur(v, locale);
  const month = formatMonth(report.reportMonth, locale);
  const lead = report.contacts.find((c) => c.role === "lead");

  /**
   * Kopfblock wie im HTML-Bericht: Gattung, Kunde, Monat.
   *
   * Vorher stand hier dieselbe Zeile wie im Betreff („Kunde — Statusberichte
   * Juli 2026") und wirkte im Fließtext wie ein nicht ersetzter Platzhalter
   * (Befund B6). Jetzt ist es erkennbar eine Berichtsüberschrift und
   * wiederholt den Betreff nicht.
   */
  const title = `${t("pages.statusReports.report.eyebrow")} — ${month}`;
  lines.push(
    title,
    "=".repeat(title.length),
    report.customerName,
    ...(lead
      ? [`${t("pages.statusReports.report.serviceLead")}: ${lead.fullName}`]
      : []),
    "",
  );

  const highlightCount =
    report.highlights.length +
    report.doneTickets.filter((ticket) => ticket.highlight).length;
  if (highlightCount > 0) {
    lines.push(
      `${
        highlightCount > 1
          ? t("pages.statusReports.report.highlightsOfMonth")
          : t("pages.statusReports.report.highlightOfMonth")
      }:`,
    );
    for (const ticket of report.doneTickets.filter((x) => x.highlight)) {
      const key = ticket.jiraKey ? `${ticket.jiraKey} — ` : "";
      const value = ticket.valueDescription ? ` (${ticket.valueDescription})` : "";
      lines.push(`  • ${key}${ticket.summary}${value}`);
    }
    for (const h of report.highlights) lines.push(`  • ${h.body}`);
    lines.push("");
  }

  if (report.budgets.length > 0) {
    lines.push(`${t("pages.statusReports.report.budgetSection")}:`);
    for (const b of report.budgets) {
      // „12.600 € volumen, 14.175 € abgerufen, -1.575 € rest" stand wörtlich im
      // Entwurf: kleingeschriebene Feldnamen hinter den Zahlen, kein deutscher
      // Satz (Befund B5). Jetzt steht die Bezeichnung vor ihrem Wert.
      lines.push(
        `  • ${b.name}: ${t("pages.statusReports.volume")} ${eur(b.budgetEur)}, ` +
          `${t("pages.statusReports.used")} ${eur(b.billedEur)}, ` +
          `${t("pages.statusReports.left")} ${eur(b.leftEur)}`,
      );
    }
    // Stichtag auch im Klartext — er geht als Mail-Entwurf und ueber die
    // Zwischenablage denselben Weg zum Kunden wie das HTML (Befund B3).
    if (report.budgetAsOf) {
      lines.push(
        `${t("pages.statusReports.report.budgetAsOf")}: ${formatIsoDate(report.budgetAsOf)}`,
      );
    }
    lines.push("");
  }

  if (report.hours != null) {
    lines.push(
      `${t("pages.statusReports.report.bookedHours")} ${month}: ${formatHours(
        report.hours,
        locale,
      )}`,
      "",
    );
  }

  for (const [label, tickets, emptyLabel] of [
    [
      t("pages.statusReports.report.openSection"),
      report.openTickets,
      t("pages.statusReports.report.noOpenTickets"),
    ] as const,
    [
      `${t("pages.statusReports.report.doneSection")} – ${month}`,
      report.doneTickets,
      t("pages.statusReports.report.noDoneTickets"),
    ] as const,
  ]) {
    lines.push(`${label}:`);
    if (tickets.length === 0) {
      lines.push(`  ${emptyLabel}`);
    }
    for (const ticket of tickets) {
      const key = ticket.jiraKey ? `${ticket.jiraKey} — ` : "";
      const status = ticket.ticketStatus ? ` (${ticket.ticketStatus})` : "";
      // Das Loesungsdatum steht auch in der Textfassung: Sie geht denselben
      // Weg zum Kunden, und bei sechzig erledigten Vorgaengen ist es dort
      // genauso der Anker wie im HTML.
      const resolved = ticket.resolvedOn
        ? ` [${formatIsoDate(ticket.resolvedOn)}]`
        : "";
      lines.push(`  • ${key}${ticket.summary}${status}${resolved}`);
      // „Status und naechste Schritte" auch im Klartext: Die Textfassung geht
      // denselben Weg zum Kunden wie das HTML, und was dort steht, gehoert
      // hierhin. Eingerueckt unter den Vorgang, damit die Zuordnung stimmt.
      const schritte = ticket.nextSteps?.trim();
      if (schritte) lines.push(`      ${schritte}`);
    }
    lines.push("");
  }

  if (report.contacts.length > 0) {
    lines.push(`${t("pages.statusReports.report.contactsSection")}:`);
    for (const c of report.contacts) {
      const absences =
        c.absences.length === 0
          ? t("pages.statusReports.report.noAbsences")
          : c.absences
              .map((a) =>
                a.dateFrom === a.dateTo
                  ? formatIsoDate(a.dateFrom)
                  : `${formatIsoDate(a.dateFrom)} – ${formatIsoDate(a.dateTo)}`,
              )
              .join(", ");
      lines.push(`  • ${c.fullName}: ${absences}`);
    }
    lines.push("");
  }

  if (report.events.length > 0) {
    lines.push(`${t("pages.statusReports.report.eventsSection")}:`);
    for (const e of report.events) {
      lines.push(`  • ${formatIsoDate(e.date)} — ${e.title}`);
    }
    lines.push("");
  }

  return lines.join("\n").trimEnd();
}

/** `2026-07-31` → `31.07.2026`, wie im Bericht. */
function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Betreff des Entwurfs.
 *
 * „Statusberichte" (Plural) stand darin, weil der Name des **Menüpunkts**
 * durchgereicht wurde. Es ist aber ein Bericht, kein Sammelband (Befund B6);
 * deshalb ein eigener Schlüssel für die Gattung des Schriftstücks.
 *
 * **Fester Vorspann statt Kundenname, und ein Doppelpunkt statt eines
 * Gedankenstrichs.** Die abgelöste App schrieb „mindsquare HR Service -
 * Statusbericht März 2026", und genau diese Form ist beim Empfänger seit
 * Jahren eingeführt. Der Kundenname stand nur kurz darin und half niemandem:
 * Der Kunde weiß, wer er ist. Der Doppelpunkt ist ausdrücklich gewünscht
 * (Rückmeldung 2026-09-07).
 */
export function buildMailSubject(
  report: StatusReportResponse,
  locale: string,
  t: Translate,
): string {
  return `${t("pages.statusReports.subjectPrefix")}: ${t(
    "pages.statusReports.reportName",
  )} ${formatMonth(report.reportMonth, locale)}`;
}

/**
 * Anrede, Einleitung, Platz fuer den Bericht, Grussformel.
 *
 * Der Bericht steht bewusst NICHT drin: `mailto:` traegt ausschliesslich
 * Klartext — die gestaltete Tabelle waere ohnehin verloren —, und Windows
 * kappt lange `mailto:`-Ziele, sodass beim Empfaenger abgeschnittener Text
 * ankommt. Genau das war im Betrieb zu sehen. Die abgeloeste App macht es
 * deshalb ebenso: Bericht als HTML in die Zwischenablage, Mail nur mit dem
 * Anschreiben, Einfuegen mit Strg+V (`openEmail` in deren `index.html`).
 */
/**
 * Wer den Entwurf unterschreibt.
 *
 * Der Absender zuerst — er schreibt die Mail. Fehlt er (nicht angemeldet, kein
 * Name gepflegt), tritt der Service Lead des Berichts an seine Stelle: Der
 * Entwurf soll unter keinen Umstaenden mit einer nackten Grussformel enden,
 * hinter der man den Namen von Hand nachtragen muss.
 */
function signatur(
  report: StatusReportResponse,
  senderName: string | null | undefined,
): string {
  const absender = senderName?.trim();
  if (absender) return absender;
  const lead = report.contacts.find((c) => c.role === "lead");
  return lead?.fullName.trim() ?? "";
}

export function buildMailBody(
  report: StatusReportResponse,
  locale: string,
  t: Translate,
  senderName?: string | null,
): string {
  const monthName = formatMonth(report.reportMonth, locale);

  // Anrede wie im Original: „Du" nimmt den Vornamen, „Sie" die förmliche
  // Formel. Eine frei gepflegte Anrede sticht beide — sie ist der Weg zu
  // „Sehr geehrte Frau Müller" bei einem festen Ansprechpartner (Befund U4)
  // und steht seitdem auch in den Einstellungen.
  const isDu = report.config.salutationMode === "du";
  const greeting =
    report.config.salutation?.trim() ||
    (isDu
      ? anredeDu(report.config.salutationName, t)
      : t("pages.statusReports.greetingSie"));

  // Mehrere Empfänger lesen mit — dann redet der Text sie auch als mehrere an.
  // Maßgeblich ist die Zahl der EMPFÄNGER, nicht die der gepflegten Namen: Wer
  // die Mail bekommt, entscheidet, ob „du" oder „ihr" richtig ist. Im
  // Sie-Modus gibt es nichts zu unterscheiden — „erhalten Sie" gilt für eine
  // wie für mehrere Personen.
  const mehrere = report.config.recipients.length > 1;
  const intro = isDu
    ? t(
        mehrere
          ? "pages.statusReports.introDuPlural"
          : "pages.statusReports.introDu",
        { month: monthName },
      )
    : t("pages.statusReports.introSie", { month: monthName });

  // Leerzeilen als Platz fuer den eingefuegten Bericht, darunter die
  // Grussformel — wie in der Altanwendung immer „Viele Gruesse".
  //
  // Und darunter der NAME. Vorher endete der Entwurf mit „Viele Gruesse" und
  // sonst nichts; wer ihn abschickte, musste seinen eigenen Namen von Hand
  // nachtragen — oder vergass es (Rueckmeldung 04.09.).
  const unterschrift = signatur(report, senderName);
  const gruss = t("pages.statusReports.closing");
  // `mitKomma`: Die Anrede kann schon auf ein Satzzeichen enden — bei frei
  // gepflegter Anrede tut sie das oft. Ein unbedingt angehängtes Komma ergab
  // dann „Hallo Christopher, Hallo Max,," (Entwurf vom 07.09.2026).
  return `${mitKomma(greeting)}\n\n${intro}\n\n\n${gruss},${
    unterschrift ? `\n${unterschrift}` : ""
  }`;
}

/**
 * Die Du-Anrede über einen oder mehrere Vornamen.
 *
 * `salutationName` ist EIN Feld und trägt bei mehreren Ansprechpartnern eine
 * Liste: „Christopher, Max" wird zu „Hallo Christopher, hallo Max". Das zweite
 * `hallo` schreibt sich klein — es steht mitten im Satz, nicht am Anfang.
 *
 * Warum kein Feld je Person: Die Empfänger stehen als Adressen in der Konfig,
 * die Namen dazu pflegt niemand doppelt. Ein Komma ist die Trennung, die man
 * ohnehin hinschreibt — vorher stand deshalb wörtlich „Christopher, Hallo Max,"
 * im Namensfeld, samt mitgetipptem „Hallo" und Komma. Das Feld tut jetzt, was
 * die Pflege ohnehin versucht hat.
 */
function anredeDu(
  salutationName: string | null | undefined,
  t: Translate,
): string {
  const hallo = t("pages.statusReports.greetingDu");
  const namen = (salutationName ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean);
  if (namen.length === 0) return hallo;
  const [erster, ...weitere] = namen;
  return [
    `${hallo} ${erster}`,
    ...weitere.map((n) => `${hallo.toLocaleLowerCase("de-DE")} ${n}`),
  ].join(", ");
}

/** Ein Komma anhängen — außer die Anrede trägt schon ein Satzzeichen. */
function mitKomma(anrede: string): string {
  return /[,;:!?]$/.test(anrede.trimEnd()) ? anrede.trimEnd() : `${anrede},`;
}

/**
 * Derselbe Entwurf, aber in Outlook im Web statt in Outlook Desktop.
 *
 * **Warum es einen zweiten Weg gibt.** Beim Einfügen des Berichts läuft das
 * Markup durch einen Filter, und die beiden Programme filtern verschieden
 * scharf: Outlook Desktop streicht modernes CSS zusammen — gerundete Ecken,
 * Schatten, alles, was die Word-Engine nicht kennt — und zwar **endgültig**,
 * bevor die Mail rausgeht. Outlook im Web läuft im Browser und lässt es
 * stehen.
 *
 * Der Unterschied wirkt beim EMPFÄNGER: Was Desktop beim Einfügen wegwirft,
 * ist für alle weg, auch für Leser am Handy oder im Web. Was Outlook im Web
 * stehen lässt, sehen genau diese Leser — und wer den Bericht in Outlook
 * Desktop öffnet, bekommt dieselbe solide Fassung wie vorher. Der Weg über
 * das Web kostet also nichts und kann nur mehr erhalten.
 *
 * **Was er nicht kann.** Er macht nicht aus Outlook Desktop einen modernen
 * Renderer. Wer die Mail dort liest, sieht weiterhin die Word-Engine ohne
 * Rundungen — das entscheidet das Programm des Empfängers, nicht der Weg des
 * Absenders.
 *
 * Der Bericht selbst reist weiter über die Zwischenablage: In die Adresse
 * passt nur Text, und ein 17.000 Zeichen langes Markup schon gar nicht.
 */
export function buildOutlookWebHref(
  report: StatusReportResponse,
  locale: string,
  t: Translate,
  senderName?: string | null,
): string {
  const params = new URLSearchParams({
    to: report.config.recipients.join(";"),
    subject: buildMailSubject(report, locale, t),
    body: buildMailBody(report, locale, t, senderName),
  });
  if (report.config.ccRecipients.length > 0) {
    params.set("cc", report.config.ccRecipients.join(";"));
  }
  // Wie bei `mailto:`: `URLSearchParams` kodiert Leerzeichen als "+", und der
  // Entwurf uebernimmt das WOERTLICH. In der ersten Fassung stand deshalb
  // "Aggerverband+—+Statusbericht+August+2026" im Betreff und der ganze
  // Anschreibetext mit Pluszeichen statt Leerzeichen in der Mail.
  return `https://outlook.office.com/mail/deeplink/compose?${params
    .toString()
    .replace(/\+/g, "%20")}`;
}

/**
 * `mailto:`-Entwurf mit Empfaengern, CC, Betreff und Anschreiben — ohne den
 * Bericht (s. `buildMailBody`). Der Aufrufer legt ihn vorher als HTML in die
 * Zwischenablage.
 */
export function buildMailtoHref(
  report: StatusReportResponse,
  locale: string,
  t: Translate,
  senderName?: string | null,
): string {
  const to = report.config.recipients.join(",");
  const params = new URLSearchParams({
    subject: buildMailSubject(report, locale, t),
    body: buildMailBody(report, locale, t, senderName),
  });
  if (report.config.ccRecipients.length > 0) {
    params.set("cc", report.config.ccRecipients.join(","));
  }
  // URLSearchParams kodiert Leerzeichen als "+", was Mailprogramme wörtlich
  // übernehmen — für mailto muss es %20 sein.
  return `mailto:${to}?${params.toString().replace(/\+/g, "%20")}`;
}
